import { useMemo } from 'react';
import { BillingCycle, Service, Subscription } from '~/types/models';
import { PlanCard } from '~/types/models/plasmic';
import { StripeCoupon } from '~/types/stripe';
import { getDiscountedPrice } from '../utils';
import { PricingPlanCardProps } from '../Components/Pricing/PricingPlanCards/PricingPlanCard';
import useCurrentTeam from '~/react-hooks/use-current-team';

export interface UseMappedPlanCardsHookArgs {
  plasmicPlanCards?: PlanCard[];
  subscriptions?: Subscription[];
  billingCycle?: BillingCycle;
  coupon?: StripeCoupon;
  onPlanSelect?: (planSlug: string, planCard: PlanCard) => void;
}

export const useMappedPlanCards = ({
  plasmicPlanCards,
  coupon,
  subscriptions,
  billingCycle,
  onPlanSelect,
}: UseMappedPlanCardsHookArgs) => {
  const { payment_gateway: paymentGateway } = useCurrentTeam();

  return useMemo(() => {
    if (!plasmicPlanCards || !subscriptions) return;

    return plasmicPlanCards
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((planCard) => {
        const plan = planCard.plans.find(
          (value) => value.gateway == paymentGateway
        );

        const showCurrentPlanTheme = checkIfOnCurrentPlan({
          subscriptions,
          billingCycle,
          monthlyPlanSlug: plan?.monthlySlug,
          annualPlanSlug: plan?.annualSlug,
        });

        return {
          ...planCard,
          details: {
            ...planCard.details,
            monthlyPrice: getDiscountedPrice({
              price: planCard?.details?.monthlyPrice,
              coupon,
            }),
            annualPrice: getDiscountedPrice({
              price: planCard?.details?.annualPrice,
              coupon,
            }),
          },
          showCurrentPlanTheme,
          onCtaClick: () => {
            if (!plan) return;

            if (billingCycle === BillingCycle.Monthly) {
              onPlanSelect?.(plan.monthlySlug, planCard);
            } else if (billingCycle === BillingCycle.Yearly) {
              onPlanSelect?.(plan.annualSlug, planCard);
            }
          },
        } as PricingPlanCardProps;
      });
  }, [
    coupon,
    plasmicPlanCards,
    subscriptions,
    billingCycle,
    onPlanSelect,
    paymentGateway,
  ]);
};

export interface CheckIfOnCurrentPlanProps {
  subscriptions: Subscription[];
  billingCycle?: BillingCycle;
  monthlyPlanSlug?: string;
  annualPlanSlug?: string;
}

export function checkIfOnCurrentPlan({
  subscriptions,
  billingCycle,
  monthlyPlanSlug,
  annualPlanSlug,
}: CheckIfOnCurrentPlanProps) {
  return !!subscriptions.find((subscription) => {
    if (subscription?.is_free) return false;

    // Check the current billing cycle and returns the checking condition accordingly
    if (billingCycle === BillingCycle.Monthly) {
      return monthlyPlanSlug == subscription.slug;
    } else if (billingCycle === BillingCycle.Yearly) {
      return annualPlanSlug == subscription.slug;
    }

    return false;
  });
}

export interface GetPlanCardViaServiceArgs {
  service: Service;
  planCards?: PricingPlanCardProps[];
}

export function getPlanCardViaService({
  service,
  planCards,
}: GetPlanCardViaServiceArgs) {
  return planCards?.filter((planCard) => planCard.service === service)?.[0];
}

export interface GetPricePlanSlugArgs {
  planSlug: string;
  planCard?: PlanCard;
  coupon?: StripeCoupon;
}

export function getPricePlanSlug({
  planSlug,
  planCard,
  coupon,
}: GetPricePlanSlugArgs): number {
  if (planSlug === planCard?.details?.monthlyPlanSlug) {
    return getDiscountedPrice({
      price: planCard?.details?.monthlyPrice,
      coupon,
    });
  }

  if (planSlug === planCard?.details?.annualPlanSlug) {
    return getDiscountedPrice({
      price: planCard?.details?.annualPrice,
      coupon,
    });
  }

  return 0;
}
